import type {TFunction} from "i18next";
import {AutomationExecution, ExecutionStep, NodeType} from "crqlar-react-common/dist/models/automation/automation";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import {isFanOutNode} from "../config/nodeCategories";
import {ownSteps, parseStepResult} from "../utils/executionSteps";

const EMPTY_VALUE = '-';

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export const formatDate = (date?: Date | string) => {
    if (!date) return EMPTY_VALUE;

    const parsed = date instanceof Date ? date : new Date(date);
    return Number.isNaN(parsed.getTime()) ? EMPTY_VALUE : formatDateDMYHM(parsed);
};

export const formatDuration = (startedAt?: Date | string, completedAt?: Date | string) => {
    if (!startedAt) return EMPTY_VALUE;

    const start = new Date(startedAt).getTime();
    const end = completedAt ? new Date(completedAt).getTime() : Date.now();
    if (Number.isNaN(start) || Number.isNaN(end)) return EMPTY_VALUE;

    // A run parked at a Wait lasts days: "2.0d", not "2880.0m".
    const durationMs = end - start;
    if (durationMs < SECOND_MS) return `${durationMs}ms`;
    if (durationMs < MINUTE_MS) return `${(durationMs / SECOND_MS).toFixed(1)}s`;
    if (durationMs < HOUR_MS) return `${(durationMs / MINUTE_MS).toFixed(1)}m`;
    if (durationMs < DAY_MS) return `${(durationMs / HOUR_MS).toFixed(1)}h`;
    return `${(durationMs / DAY_MS).toFixed(1)}d`;
};

export const formatEnumValue = (value?: string) => {
    if (!value) return '';

    return value
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

// `PROFILE` is a fan-out child's guest, `USER` a team member, the rest what an event trigger started on.
// An unknown code falls back to the enum formatting.
const TRIGGER_ENTITY_LABEL_KEYS: Record<string, string> = {
    PROFILE: 'Guest',
    USER: 'Team member',
    RESERVATION: 'Reservation',
    SURVEY_SUBMISSION: 'Survey response',
    SURVEY_INVITATION: 'Survey invitation',
    FORM: 'Form',
};

export const triggerEntityLabel = (entityType: string, t: TFunction): string => {
    const key = TRIGGER_ENTITY_LABEL_KEYS[entityType];
    return key ? t(key) : formatEnumValue(entityType);
};

export const formatStepResult = (result: unknown): string => {
    if (result === undefined || result === null) return '';

    if (typeof result !== 'string') {
        try {
            return JSON.stringify(result, null, 2);
        } catch {
            return String(result);
        }
    }

    try {
        return JSON.stringify(JSON.parse(result), null, 2);
    } catch {
        return result;
    }
};

const nameIn = (step?: ExecutionStep): string | undefined => {
    const result = parseStepResult(step?.result);
    return [result.firstName, result.lastName].filter(Boolean).join(' ') || undefined;
};

/** The name the fan-out step recorded for a child run; undefined when the payload leaves the steps out. */
export const fanOutMemberName = (execution: AutomationExecution): string | undefined => {
    const steps = execution.executionSteps ?? [];
    // A member run inside a member run carries both people; its own is the step it starts from.
    const own = execution.startFromNodeId
        ? [...steps].reverse().find(step => step.nodeId === execution.startFromNodeId)
        : undefined;

    return nameIn(own) ?? steps.map(nameIn).find(Boolean);
};

/**
 * The person a run is about: a Team members run's colleague, else the trigger's guest (`context.guest`), else a
 * fan-out child's member. A fan-out parent or a scheduled run has no name.
 */
export const runGuestName = (execution: AutomationExecution): string | undefined => {
    // A member run copies its parent's context, so `context.guest` there is still the trigger's guest.
    if (execution.triggerEntityType === 'USER') return fanOutMemberName(execution);

    const guest = execution.context?.guest;
    const name = guest && typeof guest === 'object'
        ? [guest.firstName, guest.lastName].filter(Boolean).join(' ')
        : '';
    if (name) return name;

    return execution.parentExecutionId ? fanOutMemberName(execution) : undefined;
};

/**
 * The runs list's Guest cell: the person over the run's kind, or the kind alone. A run about no one that fanned out
 * (Run for everyone, a schedule) shows the member runs it started over the step that started them.
 */
export const runGuestCell = (
    execution: AutomationExecution,
    t: TFunction
): {title: string; detail?: string} | undefined => {
    if (execution.triggerEntityType) {
        const kind = triggerEntityLabel(execution.triggerEntityType, t);
        const name = runGuestName(execution);
        return name ? {title: name, detail: kind} : {title: kind};
    }

    const fanOut = ownSteps(execution).find(step => isFanOutNode(step.nodeType));
    if (!fanOut) return undefined;

    const count = Number(parseStepResult(fanOut.result).queued) || 0;
    return {
        title: fanOut.nodeType === NodeType.TEAM_MEMBERS
            ? t('Run team member count', {count})
            : t('Run guest count', {count}),
        detail: fanOut.nodeName || undefined
    };
};

/** Where a Continue if ended the run (`evaluated: false`). Such a run is Completed, not failed. */
export const stoppedAtText = (execution: AutomationExecution | undefined, t: TFunction): string | undefined => {
    const step = execution && ownSteps(execution).find(entry => (
        entry.nodeType === NodeType.CONTINUE_IF && parseStepResult(entry.result).evaluated === false
    ));

    return step ? t('Stopped at “{{step}}”', {step: step.nodeName || t('Continue if')}) : undefined;
};

/** A stopped Continue if's first failing contact limit, as the API reports it (`ContinueIfExecutor`). */
export interface ContactBlock {
    /** EMAIL, PUSH or ANY. */
    channel?: string;
    maximum?: number;
    /** Null: ever. */
    days?: number | null;
    lastContactAt?: string | Date | null;
}

// "Last": `lastContactAt` is the newest of the contacts the limit counted, which can be several.
const LAST_CONTACT_KEYS: Record<string, string> = {
    EMAIL: 'Last emailed on {{date}}.',
    PUSH: 'Last sent a push notification on {{date}}.',
};

/** Why a Continue if stopped on a contact limit, in words with the local time; undefined without a block. */
export const contactBlockText = (block: ContactBlock | null | undefined, t: TFunction): string | undefined => {
    if (!block || typeof block !== 'object') return undefined;

    const date = block.lastContactAt ? formatDate(block.lastContactAt) : EMPTY_VALUE;
    const contact = date !== EMPTY_VALUE
        ? t(LAST_CONTACT_KEYS[block.channel ?? ''] ?? 'Last contacted on {{date}}.', {date})
        : t('Already contacted.');

    const maximum = Number(block.maximum) || 1;
    const limit = block.days
        ? t('Contact limit in days', {maximum, count: block.days})
        : t('The limit is {{maximum}} in total.', {maximum});

    return `${contact} ${limit}`;
};

/** The flag sits on the trigger step and fan-out children copy it. API dry runs count as tests too. */
export const isTestRunExecution = (execution?: AutomationExecution): boolean => {
    const trigger = execution?.executionSteps?.[0];
    return !!trigger && (!!trigger.isTestRun || !!trigger.isDryRun);
};

const textList = (value: unknown): string[] => (Array.isArray(value) ? value : [])
    .filter(entry => (typeof entry === 'string' || typeof entry === 'number') && String(entry).trim() !== '')
    .map(String);

const numberOf = (value: unknown): number | undefined => {
    const number = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
    return Number.isFinite(number) ? number : undefined;
};

/** A Survey report step's result in one line, and the files it built; undefined for a result without figures. */
export const surveyReportStepText = (
    result: Record<string, any>,
    t: TFunction
): {text: string; files: string[]} | undefined => {
    const responses = numberOf(result.responses);
    const score = numberOf(result.score);
    const nps = numberOf(result.nps);
    const parts = [
        typeof result.surveyName === 'string' ? result.surveyName : '',
        typeof result.period === 'string' ? result.period : '',
        responses !== undefined ? `${t('Responses')} ${responses}` : '',
        score !== undefined ? `${t('Score')} ${Math.round(score)}` : '',
        nps !== undefined ? `${t('NPS')} ${Math.round(nps)}` : ''
    ].filter(Boolean);

    return parts.length ? {text: parts.join(' · '), files: textList(result.fileNames)} : undefined;
};

export type TeamEmailOutcome = 'sent' | 'held' | 'refused' | 'unknown' | 'skipped';

/**
 * The API's `status` for a Send team email step: `sent`, `held` (a run that holds emails), `failed` (the mail service
 * refused it or the call failed; its code is in `statusCode`), `delivery_unknown` (a run resumed after a crash in the
 * middle of sending: never sent twice) or `skipped` (the member has no email address). A bare status code is read too.
 */
export const teamEmailOutcome = (result: Record<string, any>): TeamEmailOutcome | undefined => {
    switch (String(result.status ?? '').trim().toLowerCase()) {
        case 'sent': return 'sent';
        case 'held': return 'held';
        case 'failed': return 'refused';
        case 'delivery_unknown': return 'unknown';
        case 'skipped': return 'skipped';
    }
    if (result.deliveryUnknown === true) return 'unknown';
    const code = numberOf(result.statusCode ?? result.status);
    if (code !== undefined) return code >= 200 && code < 300 ? 'sent' : 'refused';
    return undefined;
};

/** What a Send team email step did, in words; undefined for a result that says nothing about the send. */
export const teamEmailStepText = (
    result: Record<string, any>,
    t: TFunction
): {outcome: TeamEmailOutcome; text: string; files: string[]} | undefined => {
    const outcome = teamEmailOutcome(result);
    if (!outcome) return undefined;

    const address = typeof result.sentTo === 'string' && result.sentTo.trim() ? result.sentTo.trim() : t('the team member');
    const name = typeof result.memberName === 'string' && result.memberName.trim() ? result.memberName.trim() : t('the team member');
    const code = numberOf(result.statusCode ?? result.status);
    const text = (() => {
        switch (outcome) {
            case 'sent': return t('Sent to {{address}}.', {address});
            case 'held': return t('Not sent: this run holds emails.');
            case 'refused': return code !== undefined
                ? t('The mail service refused the email to {{address}} (status {{status}}).', {address, status: code})
                : t('Sending the email to {{address}} failed.', {address});
            case 'unknown': return t('Unknown whether the email to {{address}} went out: the run stopped while sending, and it is never sent twice.', {address});
            case 'skipped': return t('Not sent: {{name}} has no email address.', {name});
        }
    })();

    return {outcome, text, files: outcome === 'sent' ? textList(result.attachmentNames) : []};
};

/** Why Send Email skipped its guest (`NO_EMAIL_ADDRESS`): such a run completes rather than fails. */
export const emailSkipReason = (execution: AutomationExecution): string | undefined => (execution.executionSteps ?? [])
    .filter(step => step.nodeType === NodeType.SEND_EMAIL)
    .map(step => parseStepResult(step.result))
    .find(result => result.skipped === true)?.reason;
